import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { useSeo, buildCanonical } from '@/hooks/useSeo';

const positions = [
  {
    icon: 'ri-crosshair-2-line',
    title: '只讲通用套路',
    desc: '把跨商家重复出现的坑点归纳成模式，而不是针对某个具体品牌。这样内容才经得起时间。',
  },
  {
    icon: 'ri-list-check-2',
    title: '输出可执行清单',
    desc: '每份清单最后都能落到具体动作：先确认什么、截图什么、问清什么，看完就能用。',
  },
  {
    icon: 'ri-refresh-line',
    title: '持续复核更新',
    desc: '平台规则和促销方式一直在变，清单会定期回看和修订，过时的内容会被替换而不是保留。',
  },
];

const principles = [
  { no: '01', title: '宁可少，不要误导', desc: '没有依据的判断不写成结论，写不清楚的地方宁可留白，也不猜。' },
  { no: '02', title: '就事论事，不针对人', desc: '我们讨论的是套路和规则，不是攻击某家商家，也不接受带有攻击性的投稿。' },
  { no: '03', title: '给方法，不给答案', desc: '同一件事在不同平台的结果可能不同，我们更愿意教你怎么判断，而不是替你下结论。' },
  { no: '04', title: '让普通人看得懂', desc: '尽量不用行业黑话，把复杂的规则翻译成一句人话，让你在下单那一刻想得起来。' },
];

const voices = [
  {
    quote: '看完预售那份清单，才发现我一直分不清「定金」和「订金」。今年大促终于没被套住。',
    name: '林然',
    role: '网购重度用户',
  },
  {
    quote: '以旧换新之前把激活日期和电池循环都查了一遍，果然有台机器激活比收货早了两个月。',
    name: '周屿',
    role: '数码爱好者',
  },
  {
    quote: '临期食品那份很实在，讲清楚了哪些能囤哪些别碰。按消耗速度买之后确实省了不少。',
    name: '陈曦',
    role: '家庭采购',
  },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们｜看料避雷社 内容原则与团队定位',
    description:
      '了解看料避雷社的品牌定位、内容原则与联系方式。我们专注把消费场景里的坑点整理成可执行的避雷清单，只讲通用套路，持续复核更新，让普通人也能轻松排雷。',
    keywords: '看料避雷社,关于我们,内容原则,消费避雷,内容定位',
    path: '/about',
    image: 'https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20open%20notebooks%20and%20a%20desk%20lamp%20on%20a%20cream%20table%2C%20amber%20and%20brown%20tones%2C%20minimal%20clean%20composition&width=1200&height=630&seq=og-kanliao-about&orientation=landscape',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于看料避雷社',
        url: buildCanonical('/about'),
        inLanguage: 'zh-CN',
        description: '看料避雷社的品牌定位、内容原则与联系方式。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '看料避雷社',
        url: buildCanonical('/'),
        email: 'contact@kanliaobilei.com',
        description: '面向普通消费者的避雷指南内容站。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: buildCanonical('/') },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: buildCanonical('/about') },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <Breadcrumbs items={[{ name: '首页', to: '/' }, { name: '关于我们' }]} />

      <main className="flex-1">
        <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100/60 border-b border-background-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-label tracking-widest uppercase text-accent-600 mb-3">
                <span className="w-6 h-px bg-accent-400"></span>
                关于我们
              </span>
              <h1 className="font-heading text-3xl md:text-5xl font-black text-foreground-950 leading-tight">
                我们只做一件事：把坑讲清楚
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-700 leading-loose font-body">
                看料避雷社是一个由编辑团队维护的消费避雷内容站。我们不卖货、不做导购，只把网购、美妆、数码、食饮、旅宿、教育、理财、家装等场景中反复出现的坑点，
                整理成「风险等级 + 常见套路 + 危险信号 + 避雷清单」的结构化内容。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-loose font-body">
                目标是让你在按下付款键、签下名字、走出门之前，多一个想得起来、看得懂、能立刻执行的检查清单。
                避雷不需要记住所有骗局，只要记住几个关键动作。
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/guides"
                  className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-shield-check-line"></i>
                  开始浏览清单
                </Link>
                <a
                  href="mailto:contact@kanliaobilei.com"
                  rel="nofollow"
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-3 text-sm font-label text-foreground-800 hover:bg-background-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-mail-line"></i>
                  联系我们
                </a>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden border border-background-200 h-72 md:h-96 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20open%20notebooks%2C%20paper%20files%20and%20a%20cup%20of%20tea%20on%20a%20cream%20wooden%20desk%2C%20soft%20afternoon%20light%2C%20amber%20and%20beige%20tones%2C%20minimal%20calm%20composition&width=1000&height=800&seq=about-kanliao-team&orientation=landscape"
                alt="看料避雷社 编辑工作台 内容整理场景"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">网站定位</h2>
          <p className="text-sm md:text-base text-foreground-600 font-body max-w-3xl mb-8">
            我们更接近一本随时可以翻的「消费避雷手册」，而不是资讯流或种草社区。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {positions.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-6">
                <span className="w-11 h-11 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-xl">
                  <i className={item.icon}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">内容原则</h2>
          <p className="text-sm md:text-base text-foreground-600 font-body max-w-3xl mb-8">
            这几条是我们决定「写什么、不写什么」的标准，也是内容能被长期信任的前提。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {principles.map((item) => (
              <div key={item.no} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <span className="font-heading text-2xl font-black text-accent-500/70">{item.no}</span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed font-body">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-14 md:py-20">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">读者怎么说</h2>
          <p className="text-sm md:text-base text-foreground-600 font-body max-w-3xl mb-8">
            以下来自订阅者的真实反馈，也是我们继续更新清单的动力。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {voices.map((voice) => (
              <figure key={voice.name} className="rounded-lg border border-background-200 bg-background-100/50 p-5 md:p-6 flex flex-col">
                <i className="ri-double-quotes-l text-2xl text-secondary-500"></i>
                <blockquote className="mt-3 text-sm text-foreground-700 leading-relaxed font-body flex-1">
                  {voice.quote}
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-background-200">
                  <span className="block font-heading text-sm font-bold text-foreground-950">{voice.name}</span>
                  <span className="block text-xs text-foreground-500 font-label mt-0.5">{voice.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-8 pb-16 md:pb-20">
          <div className="rounded-lg border border-primary-200 bg-primary-50 p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug">
                  有补充、有疑问，或者想纠正我们
                </h2>
                <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed font-body">
                  欢迎通过邮件联系我们，请尽量说明具体场景和你遇到的情况。有依据的反馈我们都会认真核实，并在清单更新中体现。
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="mailto:contact@kanliaobilei.com"
                    rel="nofollow"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-foreground-950 px-5 py-3 text-sm font-label text-background-50 hover:bg-foreground-800 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-mail-send-line"></i>
                    contact@kanliaobilei.com
                  </a>
                  <Link
                    to="/#subscribe"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-primary-300 bg-background-50 px-5 py-3 text-sm font-label text-primary-700 hover:bg-primary-100 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-notification-3-line"></i>
                    订阅更新提醒
                  </Link>
                </div>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <h3 className="font-heading text-sm font-bold text-foreground-950 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-primary-600"></i>
                  几点说明
                </h3>
                <ul className="space-y-3 text-sm text-foreground-600 font-body">
                  <li className="flex items-start gap-2.5">
                    <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
                    清单内容仅供参考，具体以平台规则与合同条款为准。
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
                    我们不针对任何具体商家，也不提供个案维权代理。
                  </li>
                  <li className="flex items-start gap-2.5">
                    <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5"></i>
                    发生纠纷时，建议优先通过平台客服与正规投诉渠道解决。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}